import { BasicChannel, EncField, EncFieldGroup, Encoding, Guide } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import { EncodingHelpers, RenderDataModel, VegasFrame } from '..';
import { buildDefaultEncField, VALUES_FIELD, ZoneType } from './chart-encoding-helpers';

export function isSharedAxisMember(encoding: Encoding, id: string) {
  const field = EncodingHelpers.getEncFieldById(encoding, id);
  // False if no field with that id or field has no group id defined
  if (field?.groupId === undefined) {
    return false;
  }
  // False if there is no matching group
  if (encoding.groups?.find((group) => group.id === field.groupId) === undefined) {
    return false;
  }
  // Field must be on an axis channel
  return [...encoding.x, ...encoding.x2, ...encoding.y, ...encoding.y2].some((f) => f.id === field.id);
}

/**
 * Creates a new shared axis group with the specified EncField as the sole member.
 * Shared axis groups require at least one member. The name for the shared axis
 * is auto-generated.
 * @param encoding Encoding to update
 * @param field EncField to place in new shared axis
 * @returns EncodingResult
 */
export function createSharedAxisWithEncField(encoding: Encoding, field: EncField) {
  const guide = new Guide();
  const encoder = guide.newBatchOperation(encoding);
  const groupName = getUniqueSharedAxisName(encoding);
  encoder.createSharedAxisGroup(groupName, field.id ?? '');
  return encoder.complete();
}

/**
 * Adds a new field to an existing shared axis. This is a combination
 * of adding a field to the encoding plus adding it to the shared axis
 * group.
 * @param encoding Encoding to modify
 * @param formula CompiledFormula for new field
 * @param groupId Id of the group to add it to
 * @param channel Channel for the addition
 * @param insertAt Location to insert the new field
 * @returns EncodingResult
 */
export function addFormulaToSharedAxis(
  encoding: Encoding,
  formula: CompiledFormula,
  groupId: string,
  channel: BasicChannel,
  insertAt?: number
) {
  if (formula.isNames) {
    throw new Error('Measure Names is not supported on a shared axis');
  }

  if (!(channel === 'column' || channel === 'row')) {
    throw new Error('Shared axis is only supported for row or column');
  }
  const guide = new Guide();
  let encoder = guide.newBatchOperation(encoding);
  if (formula.isValues) {
    encoder.addEncFieldToGroup({ ...VALUES_FIELD }, groupId, insertAt);
  } else {
    const defaultEncField: EncField = buildDefaultEncField({ field: formula, channel, zone: ZoneType.Aggregate });
    const encField = encoder.addEncFieldToGroup(defaultEncField, groupId, insertAt);
    // We have to get the intermediate encoding result in order to calculate the proper frame direction
    const tempEncoding = encoder.complete().encoding;
    encoder = guide.newBatchOperation(tempEncoding);
    const frame = VegasFrame.makeDefaultExprFrame(tempEncoding, formula, channel);
    encoder.setFrame(encField.id ?? '', frame);
  }
  return encoder.complete();
}

/**
 * Replaces an existing field in the encoding with a new field. The new field
 * is inserted in the same location as the existing field and added to the specified
 * group.
 * @param encoding Encoding to update
 * @param formula CompiledFormula for the new field
 * @param fieldId Id of field to replace
 * @param groupId Id of group the field is in
 * @param channel Column or Row channel where field resides
 * @returns EncodingResult
 */
export function replaceSharedAxisFieldWithNewFormula(
  encoding: Encoding,
  formula: CompiledFormula,
  fieldId: string,
  groupId: string,
  channel: BasicChannel
) {
  if (!(channel === 'column' || channel === 'row')) {
    throw new Error('Shared axis is only supported for row or column');
  }
  // Add the new field at the location of the field being replaced
  const channelFields = channel === 'row' ? [...encoding.y, ...encoding.y2] : [...encoding.x, ...encoding.x2];
  const at = channelFields.filter((f) => f.groupId === groupId).findIndex((f) => f.id === fieldId);
  let encResult = addFormulaToSharedAxis(encoding, formula, groupId, channel, at);

  // Remove the field that is being replaced
  const guide = new Guide();
  const encoder = guide.newBatchOperation(encResult.encoding);
  encoder.removeField(fieldId);

  return encoder.complete();
}

/**
 * Replaces an existing field with another existing field in the encoding.
 * This effectively moves the field to the location of the replaced field
 * @param encoding Encoding to modify
 * @param formula CompiledFormula for the field to move
 * @param fieldId Id of field to replace
 * @param groupId Id of group
 * @param channel Channel (column or row) in which the field resides
 * @returns EncodingResult
 */
export function replaceSharedAxisFieldWithFormula(
  encoding: Encoding,
  formula: CompiledFormula,
  fieldId: string,
  groupId: string,
  channel: BasicChannel
) {
  const alias = formula.isField ? formula.alias : undefined;
  if (alias === undefined) {
    throw new Error('Attempting to move a field without an alias');
  }
  if (!(channel === 'column' || channel === 'row')) {
    throw new Error('Shared axis is only supported for row or column');
  }

  const channelFields = channel === 'row' ? [...encoding.y, ...encoding.y2] : [...encoding.x, ...encoding.x2];
  const insertAt = channelFields.filter((f) => f.groupId === groupId).findIndex((f) => f.id === fieldId);

  const guide = new Guide();
  const encoder = guide.newBatchOperation(encoding);
  if (formula.isDiscrete) {
    // If we are replacing a field with an existing discrete field, we need to aggregate first.
    const aggrEncField: EncField = buildDefaultEncField({ field: formula, channel, zone: ZoneType.Aggregate });
    encoder.updateField({
      ...aggrEncField,
      id: alias,
    });
  }
  encoder.moveEncFieldToGroup(alias, groupId, insertAt);
  encoder.removeField(fieldId);
  return encoder.complete();
}

/**
 * Moves an existing field to a shared axis group.
 * @param encoding Encoding to update
 * @param compiledField Field which is being moved
 * @param groupId id of group
 * @param channel Channel of group
 * @param insertAt Position relative to group to insert the field
 * @param markFieldIds Ids of related mark fiels (if moved from marks channel)
 * @returns EncodingResult
 */
export function moveFormulaToSharedAxis(
  encoding: Encoding,
  compiledField: CompiledField,
  groupId: string,
  channel: BasicChannel,
  insertAt?: number,
  markFieldIds?: string[]
) {
  if (compiledField.isNames) {
    throw new Error('Measure Names is not supported on a shared axis');
  }
  const alias = compiledField.alias;
  if (alias === undefined) {
    throw new Error('Attempting to move a field without an alias');
  }

  if (!(channel === 'column' || channel === 'row')) {
    throw new Error('Shared axis is only supported for row or column');
  }
  const guide = new Guide();
  let encoder = guide.newBatchOperation(encoding);

  if (Array.isArray(markFieldIds) && markFieldIds.length > 0) {
    // moving all marks from mark channel to non mark channel
    markFieldIds.shift();
    encoder.removeFields(markFieldIds);
  }

  if (compiledField.isDiscrete) {
    encoder.removeField(alias);
    const defaultEncField: EncField = buildDefaultEncField({
      field: compiledField,
      channel,
      zone: ZoneType.Aggregate,
    });
    const encField = encoder.addEncFieldToGroup(defaultEncField, groupId, insertAt);
    // We have to get the intermediate encoding result in order to calculate the proper frame direction
    const tempEncoding = encoder.complete().encoding;
    encoder = guide.newBatchOperation(tempEncoding);
    const frame = VegasFrame.makeDefaultExprFrame(tempEncoding, compiledField, channel);
    encoder.setFrame(encField.id ?? '', frame);
  } else {
    encoder.moveEncFieldToGroup(alias, groupId, insertAt);
  }
  return encoder.complete();
}

/**
 * Removes a field from a shared axis
 * @param encoding Encoding to modify
 * @param field Field to remove
 * @param toChannel Channel to place the field. If not specified, the field will remain in the same channel
 * @param index Index in the channel to place the field. If not specified, it is place after the remaining group fields
 * @returns EncodingResult
 */
export function moveFieldOutOfSharedAxis(
  encoding: Encoding,
  field: EncField,
  toChannel?: BasicChannel,
  index?: number
) {
  const guide = new Guide();
  const encoder = guide.newBatchOperation(encoding);
  encoder.removeFieldFromGroup(field.id ?? '', toChannel, index);
  return encoder.complete();
}

/**
 * Moves a shared axis group and all the members to a different location and/or channel
 * @param encoding Encoding to modify
 * @param groupId Id of group to move
 * @param channel Channel to move the group to
 * @param at Field id location to move the group before
 * @returns EncodingResult
 */
export function moveSharedAxis(encoding: Encoding, groupId: string, channel: BasicChannel, at?: string) {
  const guide = new Guide();
  const encoder = guide.newBatchOperation(encoding);
  encoder.moveGroup(groupId, channel, at);
  encoder.autoEncode();
  return encoder.complete();
}

/**
 * Removes a field from the shared axis
 * @param encoding Encoding to modify
 * @param field Field to remove
 * @returns EncodingResult
 */
export function removeFieldFromSharedAxis(encoding: Encoding, field: EncField) {
  const guide = new Guide();
  const encoder = guide.newBatchOperation(encoding);
  encoder.removeFieldFromGroup(field.id ?? '');
  return encoder.complete();
}

/**
 * Ungroups/disbands a group. The fields remain and are no longer part of a shared axis
 * @param encoding Encoding to modify
 * @param groupId Id of group to ungroup
 * @returns EncodingResult
 */
export function ungroupSharedAxis(encoding: Encoding, groupId: string) {
  const guide = new Guide();
  const encoder = guide.newBatchOperation(encoding);
  encoder.disbandGroup(groupId);
  return encoder.complete();
}

/**
 * Makes a shared axis dual or a dual shared axis not dual. This works similar
 * to toggling dual axis on a field. When making the shared axis dual, it uses
 * the previous field/group as the dual axis relative.
 * @param encoding Encoding to modify
 * @param groupId Id of group
 * @returns EncodingResult
 */
export function toggleDualAxisOnSharedAxis(enc: Encoding, groupId: string) {
  const guide = new Guide();
  const encoder = guide.newBatchOperation(enc);
  encoder.toggleDualAxisOnGroup(groupId);
  return encoder.complete();
}

/**
 * Removes a shared axis group AND all of the members from the encoding
 * @param encoding Encoding to modify
 * @param groupId Id of group to remove
 * @returns EncodingResult
 */
export function removeSharedAxis(encoding: Encoding, groupId: string) {
  const guide = new Guide();
  const encoder = guide.newBatchOperation(encoding);
  encoder.removeEncFieldGroup(groupId);
  return encoder.complete();
}

/**
 * Returns an Array containing any EncFieldGroups which were removed between
 * the previous encoding and the current encoding. Returns an empty Array if
 * no groups were removed.
 */
export function getRemovedGroups(previous: Encoding, current: Encoding) {
  return previous.groups?.filter((prevGroup) => !current.groups?.some((g) => g.id === prevGroup.id)) ?? [];
}

/**
 * Build a mapping of groupId to an array of group member EncFields
 * from the specified array of EncFields
 */
export function buildGroupToMemberMap(encFields: Readonly<EncField[]>) {
  const groups = encFields.reduce((groupMap, encField) => {
    if (!encField.groupId) {
      return groupMap;
    }
    if (!groupMap.has(encField.groupId)) {
      groupMap.set(encField.groupId, []);
    }
    const groupMembers = groupMap.get(encField.groupId);
    groupMembers?.push(encField);
    return groupMap;
  }, new Map<string, EncField[]>());
  return groups;
}

/**
 * Returns an array of EncField which are the members of the specified group.
 */
export function getGroupMembersInEncoding(group: EncFieldGroup, encoding: Encoding) {
  return [...encoding.y, ...encoding.y2, ...encoding.x, ...encoding.x2].filter((f) => f.groupId === group.id);
}

export function getEncFieldRepresentingGroup(group: EncFieldGroup, encoding: Encoding, model: RenderDataModel) {
  const members = getGroupMembersInEncoding(group, encoding);
  const compiledFields = members.map((encField) => model.compileEncField(encField));
  const field = getRepresentativeField(compiledFields) ?? compiledFields[0];
  return members.find((f) => f.id === field?.alias);
}

export function getCompiledFieldRepresentingGroup(
  group: EncFieldGroup,
  encoding: Encoding,
  view: FlamingoTypes.ModeView
) {
  const members = getGroupMembersInEncoding(group, encoding);
  const compiledFields = members.map((encField) => CompiledField.fromEncField(view, encField));
  return getRepresentativeField(compiledFields);
}

function getRepresentativeField(compiledGroupMembers: CompiledField[]) {
  // Lowest common denominator for all fields in terms of formatting complexity.
  //  - Simple numeric
  //  - Continuous DatePart
  //  - Continuous DateTrunc
  let chosenField: CompiledField | undefined;
  compiledGroupMembers.some((compiledField) => {
    if (chosenField === undefined) {
      chosenField = compiledField;
      // If this field is not a date/time then we are done
      return !chosenField.isFormulaDate;
    }
    if (!compiledField.isFormulaDate) {
      chosenField = compiledField;
      return true;
    }
    return false;
  });
  return chosenField;
}

function getUniqueSharedAxisName(encoding: Encoding) {
  const baseName = 'Shared Axis';
  let suffix = 1;
  let axisName = `${baseName} ${suffix}`;
  while (encoding.groups?.some((g) => g.name === axisName)) {
    axisName = `${baseName} ${++suffix}`;
  }
  return axisName;
}
